import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { ArrowRight, Play, Share2, Trash2, Clock, Save, Lock, Globe, Users as UsersIcon, Sparkles, FileText, Layers, Shield, AlertTriangle, Activity } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { notify } from "../lib/notify";
import SEO from "../components/SEO";

const CATEGORY_ICON = {
  "Cyber & Tech Risk": Shield,
  "Data Protection": FileText,
  "Enterprise Risk": Activity,
  "AI / Model Risk": Sparkles,
  "Third-Party Risk": UsersIcon,
  "Incident Response": AlertTriangle,
  "CII / Cybersecurity": Shield,
  "Resilience": Layers,
  "Intelligence": Sparkles,
  "Framework Mapping": Layers,
  "Financial Crime": AlertTriangle,
};

export function WorkflowsLanding() {
  const [tpls, setTpls] = useState([]);
  const [mine, setMine] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/workflows/templates").then(r => setTpls(r.data.templates)).catch(() => {});
    if (user) api.get("/workflows").then(r => setMine(r.data.workflows || [])).catch(() => {});
  }, [user]);

  const save = async (tpl) => {
    if (!user) { notify.error("Sign in to save a workflow."); return; }
    try {
      const r = await api.post("/workflows", { template_id: tpl.id, name: tpl.name });
      setMine(prev => [r.data, ...prev]);
      notify.success(`"${tpl.name}" saved to your profile.`);
    } catch (e) { notify.error(e.response?.data?.detail || "Could not save."); }
  };

  const categories = [...new Set(tpls.map(t => t.category))];

  return (
    <div data-testid="page-workflows">
      <SEO
        title="Singapore GRC Workflow Templates · Run · Save · Share"
        description="12 pre-built workflow templates for Singapore CROs, DPOs and compliance leads. Run MAS TRM reviews, PDPA audits, CSRS pulses, AML self-checks and more — Claude-summarised, save to profile, schedule and share."
        keywords={["Singapore GRC workflow", "MAS TRM template", "PDPA audit", "CSRS Singapore", "compliance automation Singapore", "MAS workflow", "PDPC checklist"]}
        path="/workflows"
      />

      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">Automation · save · share</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Compliance workflows.<br /><span style={{ color: "var(--rp-accent)" }}>Pre-built for Singapore.</span></h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">Stop building MAS TRM reviews, PDPA audits and CSRS pulses from scratch every quarter. Pick a workflow, save it to your profile, schedule it or run on demand. Each run produces a shareable executive brief — written by Claude, grounded in your scored data.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#templates" className="btn-accent">Browse templates <ArrowRight size={14} /></a>
            {user && mine.length > 0 && <a href="#mine" className="btn-outline">My {mine.length} saved workflow{mine.length === 1 ? "" : "s"}</a>}
          </div>
        </div>
      </section>

      {user && mine.length > 0 && (
        <section id="mine" className="section border-b border-slate-100">
          <div className="container-rp">
            <div className="overline">Your saved workflows</div>
            <h2 className="font-display text-3xl font-semibold mt-3">Run, schedule, share.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8" data-testid="my-workflows">
              {mine.map(wf => <SavedWorkflowCard key={wf.id} wf={wf} onChanged={() => api.get("/workflows").then(r => setMine(r.data.workflows || []))} />)}
            </div>
          </div>
        </section>
      )}

      <section id="templates" className="section">
        <div className="container-rp">
          <div className="overline">Templates · {tpls.length} pre-built</div>
          <h2 className="font-display text-3xl font-semibold mt-3">Pick one that maps to your week.</h2>

          {categories.map(cat => (
            <div key={cat} className="mt-12">
              <div className="flex items-center gap-3 mb-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{cat}</div>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                {tpls.filter(t => t.category === cat).map(tpl => {
                  const Icon = CATEGORY_ICON[tpl.category] || Sparkles;
                  return (
                    <div key={tpl.id} className="rp-surface p-6 flex flex-col" data-testid={`tpl-${tpl.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <Icon size={18} style={{ color: "var(--rp-accent)" }} />
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm" style={{ background: "var(--rp-bg-subtle)", border: "1px solid var(--rp-border)" }}>{tpl.regulator}</span>
                      </div>
                      <div className="font-display text-xl font-semibold mt-4">{tpl.name}</div>
                      <p className="text-sm text-slate-600 mt-2 flex-1">{tpl.tagline}</p>
                      <div className="text-[11px] font-mono text-slate-400 mt-4 flex flex-wrap gap-x-3 gap-y-1">
                        <span><Clock size={9} className="inline" /> ~{tpl.estimated_minutes} min</span>
                        <span>· {tpl.steps.length} steps</span>
                        {tpl.frameworks?.length > 0 && <span>· {tpl.frameworks.length} frameworks</span>}
                      </div>
                      <div className="mt-5 flex gap-2">
                        <button onClick={() => save(tpl)} className="btn-accent flex-1 justify-center text-xs" data-testid={`tpl-save-${tpl.id}`}><Save size={12} /> Save to profile</button>
                        <Link to={`/workflows/templates/${tpl.id}`} className="btn-outline text-xs" data-testid={`tpl-view-${tpl.id}`}>Preview</Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SavedWorkflowCard({ wf, onChanged }) {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [showShare, setShowShare] = useState(false);

  const run = async () => {
    setRunning(true); setLastRun(null);
    try {
      const r = await api.post(`/workflows/${wf.id}/run`);
      setLastRun(r.data);
      notify.success("Workflow ran successfully.");
      onChanged?.();
    } catch (e) { notify.error(e.response?.data?.detail || "Run failed."); }
    finally { setRunning(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this saved workflow?")) return;
    try { await api.delete(`/workflows/${wf.id}`); notify.success("Deleted."); onChanged?.(); }
    catch { notify.error("Could not delete."); }
  };

  const updateShare = async (vis) => {
    try {
      await api.post(`/workflows/${wf.id}/share`, { visibility: vis });
      notify.success(`Visibility set to ${vis}.`);
      setShowShare(false);
      onChanged?.();
    } catch { notify.error("Could not update sharing."); }
  };

  const copyShare = () => {
    if (!lastRun?.share_url) return;
    const url = `${window.location.origin}${lastRun.share_url}`;
    navigator.clipboard.writeText(url).then(() => notify.success("Share link copied."));
  };

  return (
    <div className="rp-card p-6" data-testid={`saved-wf-${wf.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg font-semibold">{wf.name}</div>
          <div className="text-[11px] font-mono text-slate-400 mt-1">{wf.template_id} · {wf.run_count || 0} runs</div>
        </div>
        <div className="flex items-center gap-1">
          {wf.visibility === "public" ? <Globe size={12} className="text-emerald-600" /> : wf.visibility === "team" ? <UsersIcon size={12} className="text-amber-600" /> : <Lock size={12} className="text-slate-400" />}
        </div>
      </div>

      {wf.schedule_cron && <div className="text-xs text-slate-500 mt-3 font-mono"><Clock size={10} className="inline mr-1" />{wf.schedule_cron}</div>}

      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={run} disabled={running} className="btn-accent text-xs" data-testid={`wf-run-${wf.id}`}>
          {running ? <><Activity size={12} className="animate-pulse" /> Running…</> : <><Play size={12} /> Run now</>}
        </button>
        <button onClick={() => setShowShare(s => !s)} className="btn-outline text-xs" data-testid={`wf-share-${wf.id}`}><Share2 size={12} /> Share</button>
        <button onClick={remove} className="text-rose-600 inline-flex items-center gap-1 text-xs px-2 py-2" data-testid={`wf-del-${wf.id}`}><Trash2 size={12} /></button>
      </div>

      {showShare && (
        <div className="mt-3 p-3 rounded-sm" style={{ background: "var(--rp-bg-subtle)" }}>
          <div className="overline-muted">Set sharing visibility</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {[["private", Lock], ["team", UsersIcon], ["public", Globe]].map(([v, Ic]) => (
              <button key={v} onClick={() => updateShare(v)} data-active={wf.visibility === v} className="persona-btn text-xs"><Ic size={11} /> {v}</button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-2">Public = anyone with the run URL · Team = same email domain · Private = only you</p>
        </div>
      )}

      {lastRun && (
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="overline-muted">Latest run output</div>
          <Link to={lastRun.share_url} className="font-mono text-[11px] block mt-1 break-all" style={{ color: "var(--rp-accent)" }}>{lastRun.share_url}</Link>
          <button onClick={copyShare} className="btn-outline text-xs mt-3" data-testid={`wf-copy-${wf.id}`}><Share2 size={11} /> Copy share link</button>
        </div>
      )}
    </div>
  );
}

export function WorkflowTemplateDetail() {
  const { tpl_id } = useParams();
  const [tpl, setTpl] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get(`/workflows/templates/${tpl_id}`).then(r => setTpl(r.data)).catch(() => setTpl(null)); }, [tpl_id]);
  if (!tpl) return <div className="container-rp section text-slate-400">Loading…</div>;

  const save = async () => {
    if (!user) { nav(`/login?next=/workflows/templates/${tpl_id}`); return; }
    try {
      await api.post("/workflows", { template_id: tpl.id, name: tpl.name });
      notify.success(`"${tpl.name}" saved to your profile.`);
      nav("/workflows#mine");
    } catch (e) { notify.error(e.response?.data?.detail || "Could not save."); }
  };

  return (
    <div data-testid={`tpl-detail-${tpl.id}`}>
      <SEO
        title={`${tpl.name} · Singapore Compliance Workflow`}
        description={tpl.tagline}
        keywords={[tpl.regulator, ...(tpl.frameworks || []), "workflow", "Singapore"]}
        path={`/workflows/templates/${tpl.id}`}
      />
      <section className="border-b border-slate-200">
        <div className="container-rp py-12">
          <div className="overline">Workflow template · {tpl.category}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 tracking-tight">{tpl.name}</h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">{tpl.tagline}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-mono text-slate-500">
            <span><Clock size={11} className="inline" /> ~{tpl.estimated_minutes} min</span>
            <span>· Regulator: {tpl.regulator}</span>
            <span>· {tpl.steps.length} steps</span>
            <span>· Audience: {tpl.audience?.join(", ")}</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={save} className="btn-accent"><Save size={14} /> Save to my profile</button>
            <Link to="/workflows" className="btn-outline">All templates</Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rp max-w-3xl">
          <div className="overline">Steps</div>
          <h2 className="font-display text-2xl font-semibold mt-3">{tpl.steps.length} steps · run in order.</h2>
          <ol className="mt-8 space-y-4">
            {tpl.steps.map((s, i) => (
              <li key={`${s.label}-${i}`} className="rp-card p-5 flex gap-4">
                <div className="font-display text-3xl font-bold" style={{ color: "var(--rp-accent)" }}>{(i + 1).toString().padStart(2, "0")}</div>
                <div className="flex-1">
                  <div className="font-display text-base font-semibold">{s.label}</div>
                  <div className="text-[11px] font-mono text-slate-400 mt-1">{s.type}</div>
                </div>
              </li>
            ))}
          </ol>
          {tpl.frameworks?.length > 0 && (
            <div className="mt-12">
              <div className="overline">Frameworks referenced</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {tpl.frameworks.map(f => <Link key={f} to={`/frameworks/${f}`} className="text-[11px] font-mono px-2 py-1 rounded-sm" style={{ background: "var(--rp-bg-subtle)", border: "1px solid var(--rp-border)", color: "var(--rp-accent)" }}>{f}</Link>)}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function WorkflowRunOutput() {
  const { run_id } = useParams();
  const [run, setRun] = useState(null);
  const [err, setErr] = useState(null);
  const [errCode, setErrCode] = useState(null);
  useEffect(() => {
    api.get(`/workflows/runs/${run_id}`)
      .then(r => setRun(r.data))
      .catch(e => { setErr(e.response?.data?.detail || "Run not available."); setErrCode(e.response?.status); });
  }, [run_id]);

  if (err) return (
    <div className="container-rp section text-center" data-testid="run-error">
      <h1 className="font-display text-3xl font-bold">{err}</h1>
      {errCode === 401 && <Link to={`/login?next=/workflows/runs/${run_id}`} className="btn-accent mt-6 inline-flex">Sign in to view</Link>}
      {errCode === 403 && <p className="text-slate-500 mt-4 max-w-md mx-auto">If this is your run, open <Link to="/workflows" className="underline" style={{ color: "var(--rp-accent)" }}>your workflows</Link> and change its sharing to <strong>team</strong> or <strong>public</strong>.</p>}
      <div className="mt-6"><Link to="/workflows" className="btn-outline">Browse workflows</Link></div>
    </div>
  );
  if (!run) return <div className="container-rp section text-slate-400">Loading…</div>;
  const r = run.result || {};
  return (
    <div data-testid={`run-output-${run_id}`}>
      <SEO
        title={`${r.template_name} · Run output`}
        description={`Singapore compliance workflow output: ${r.template_name}.`}
        path={`/workflows/runs/${run_id}`}
      />
      <section className="border-b border-slate-200">
        <div className="container-rp py-12">
          <div className="overline">Workflow run · shared</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 tracking-tight">{r.template_name}</h1>
          <div className="mt-3 text-xs font-mono text-slate-400">Ran {new Date(run.ran_at).toLocaleString()} · by {run.owner_email}</div>
        </div>
      </section>
      <section className="section">
        <div className="container-rp max-w-3xl space-y-6">
          {(r.steps || []).map((s, i) => (
            <div key={`${s.label}-${i}`} className="rp-card p-6">
              <div className="overline-muted">Step {i + 1} · {s.kind}</div>
              <div className="font-display text-lg font-semibold mt-2">{s.label}</div>
              <div className="prose prose-sm mt-4 max-w-none" style={{ color: "var(--rp-ink-1)" }} dangerouslySetInnerHTML={{ __html: mdToHtml(s.output || "") }} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// Tiny safe markdown renderer (headings, bullets, bold, links, code) — no script
function mdToHtml(md) {
  if (!md) return "";
  let h = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.*)$/gm, '<h3 class="font-display text-base font-semibold mt-4">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 class="font-display text-xl font-semibold mt-6">$1</h2>')
    .replace(/^# (.*)$/gm, '<h1 class="font-display text-2xl font-bold mt-6">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="font-mono text-xs px-1 rounded" style="background:var(--rp-bg-subtle)">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline" style="color:var(--rp-accent)">$1</a>')
    .replace(/^\s*[-•]\s+(.*)$/gm, '<li>$1</li>')
    .replace(/^\s*(\d+)\.\s+(.*)$/gm, '<li>$2</li>');
  h = h.replace(/(<li>.*?<\/li>\s*)+/gs, m => `<ul class="list-disc list-inside space-y-1 my-3">${m}</ul>`);
  h = h.split(/\n{2,}/).map(p => p.startsWith("<") ? p : `<p class="my-2 leading-relaxed">${p.replace(/\n/g, "<br/>")}</p>`).join("\n");
  return DOMPurify.sanitize(h, { ADD_ATTR: ["style"] });
}

export { mdToHtml };
